/* ZingCandy mobile recommendation carousel — bidirectional swipe fix. */
(() => {
  if (window.__zingRecommendationCarousel) return;
  window.__zingRecommendationCarousel = true;
  const selector = '[data-related-products-section]';
  const mobile = window.matchMedia('(max-width: 989px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let scheduled = false;
  let stickyObserver = null;

  function setup(section) {
    if (section.dataset.zingCarouselReady === 'true') return;
    const grid = section.querySelector('ul.grid');
    if (!grid || !section.querySelector('h2')) return;
    section.dataset.zingCarouselReady = 'true';

    /* Native horizontal scrolling stays enabled in both directions. */
    grid.setAttribute('tabindex', '0');
    grid.setAttribute('aria-label', 'Recommended candies. Swipe left or right to browse.');
    const controls = document.createElement('div');
    controls.className = 'zing-reco-controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Recommendation navigation');
    const button = (direction, label, glyph) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'zing-reco-control zing-reco-control-' + direction;
      el.setAttribute('aria-label', label);
      el.textContent = glyph;
      return el;
    };
    const prev = button('prev', 'Previous recommended candy', '‹');
    const next = button('next', 'Next recommended candy', '›');
    controls.append(prev, next);
    section.append(controls);

    const cards = () => Array.from(grid.children).filter(el =>
      el.querySelector('a[href*="/products/"]')
    );
    const maxScroll = () => Math.max(0, grid.scrollWidth - grid.clientWidth);
    const stepSize = () => {
      const first = cards()[0];
      if (!first) return Math.max(100, grid.clientWidth * 0.55);
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 12;
      return first.getBoundingClientRect().width + gap;
    };
    const interacted = () => controls.classList.add('has-interacted');
    const sync = () => {
      const max = maxScroll();
      const position = Math.max(0, grid.scrollLeft);
      controls.hidden = !mobile.matches || max <= 6 || cards().length < 2 || controls.classList.contains('is-dismissed');
      prev.disabled = position <= 4;
      next.disabled = position >= max - 4;
    };
    const move = (direction) => {
      if (!mobile.matches) return;
      interacted();
      const destination = Math.min(maxScroll(), Math.max(0, grid.scrollLeft + direction * stepSize()));
      grid.scrollTo({
        left: destination,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
      window.setTimeout(sync, 450);
    };
    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    grid.addEventListener('scroll', () => {
      interacted();
      controls.classList.add('is-dismissed');
      sync();
    }, { passive: true });

    /* iOS fallback: if a deliberate horizontal drag is swallowed by a child
       link or a scroll snap, advance one card instead of trapping the user. */
    let touchStart = null;
    grid.addEventListener('touchstart', (event) => {
      if (!mobile.matches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStart = {
        x: touch.clientX, y: touch.clientY, left: grid.scrollLeft
      };
    }, { passive: true });

    grid.addEventListener('touchend', (event) => {
      if (!touchStart || !mobile.matches || !event.changedTouches.length) {
        touchStart = null;
        return;
      }
      const start = touchStart;
      touchStart = null;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (Math.abs(dx) < 38 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
      interacted();
      window.setTimeout(() => {
        if (!grid.isConnected || !mobile.matches) return;
        if (Math.abs(grid.scrollLeft - start.left) > 5) return;
        const direction = dx > 0 ? -1 : 1;
        const target = Math.min(maxScroll(), Math.max(0, start.left + direction * stepSize()));
        if (Math.abs(target - grid.scrollLeft) > 5) {
          grid.scrollTo({
            left: target,
            behavior: reducedMotion.matches ? 'auto' : 'smooth'
          });
        }
      }, 130);
    }, { passive: true });
    grid.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });
    grid.addEventListener('keydown', (event) => {
      if (!mobile.matches) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });

    if ('ResizeObserver' in window) {
      const resize = new ResizeObserver(sync);
      resize.observe(grid);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }
    const productsChanged = new MutationObserver(sync);
    productsChanged.observe(grid, { childList: true });
    sync();

    if ('IntersectionObserver' in window) {
      if (stickyObserver) stickyObserver.disconnect();
      stickyObserver = new IntersectionObserver((entries) => {
        document.body.classList.toggle(
          'zing-recos-in-view',
          mobile.matches && entries.some(entry => entry.isIntersecting)
        );
      }, { rootMargin: '0px 0px -80px 0px', threshold: 0.08 });
      stickyObserver.observe(section);
    }
  }

  function run() {
    scheduled = false;
    document.querySelectorAll(selector).forEach(setup);
    if (!mobile.matches) document.body.classList.remove('zing-recos-in-view');
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(run);
  }
  function start() {
    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule, { passive: true });
    if (mobile.addEventListener) mobile.addEventListener('change', schedule);
    document.addEventListener('shopify:section:load', schedule);
  }
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();